import type { FuseResultWithScore } from 'fuse.js';
import { colorSimilarity } from './colorSimilarity';
import { cosineSimilarity } from './cosineSimilarity';
import type { Embedding, ScoreBreakdown, ThemeEmbeddings } from './search.types';

export const SIMILARITY_THRESHOLD = 0.44;
export const SIMILARITY_TIEBREAK_THRESHOLD = SIMILARITY_THRESHOLD * 0.5;
const SIMILARITY_TIE_EPSILON = 1e-9;

type ScoreAndFilterOptions<T, F = T> = Readonly<{
  termEmbedding: Embedding;
  themes: Map<string, ThemeEmbeddings>;
  embeddingMap: Map<string, Embedding>;
  getThemeId: (item: T) => string;
  /**
   * Id used to look up the Fuse (name-match) score; defaults to `getThemeId`.
   * Set this when the embedding id differs from the real themeId — e.g.
   * per-colorway rows keyed `boldly_tomato` that still share the name `boldly`,
   * so the name match applies to every colorway row, not just the base one.
   */
  getLexicalThemeId?: (item: T) => string;
  fuseResults: ReadonlyArray<FuseResultWithScore<F>>;
  getFuseItemThemeId: (item: F) => string;
  threshold: number;
  getItemCustoms?: (item: T) => ReadonlyArray<Embedding>;
  /**
   * When provided, the per-row score breakdown is written into this map keyed
   * by `getThemeId` (the embeddings-index row, so per-colorway). Debug-only —
   * omit it and no breakdown work is retained.
   */
  collectScores?: Map<string, ScoreBreakdown>;
}>;

export function scoreAndFilterByEmbedding<T, F = T>(
  items: ReadonlyArray<T>,
  { termEmbedding, themes, embeddingMap, getThemeId, getLexicalThemeId, fuseResults, getFuseItemThemeId, threshold, getItemCustoms, collectScores }: ScoreAndFilterOptions<T, F>,
): ReadonlyArray<T> {
  const lexicalThemeId = getLexicalThemeId ?? getThemeId;
  const white = embeddingMap.get('color: white');
  const beige = embeddingMap.get('color: beige');

  const lexicalScores = new Map(fuseResults.map(r => [getFuseItemThemeId(r.item), 1 - (r.score ?? 1)]));

  const similarityMap = new Map<string, number>();
  const similarityTiebreakMap = new Map<string, number>();

  for (const item of items) {
    const themeId = getThemeId(item);
    const theme = themes.get(themeId);

    const similarityLexical = lexicalScores.get(lexicalThemeId(item)) ?? 0;
    const scoresConcept = (theme?.concepts ?? []).map(e => cosineSimilarity(termEmbedding, e));
    const similarityConcept = Math.max(0, ...scoresConcept);
    // Sum of above-floor concept scores, used to break ties when overall similarity is equal
    const conceptSumScore = scoresConcept.reduce((sum, s) => sum + Math.max(0, s - SIMILARITY_TIEBREAK_THRESHOLD), 0);
    const itemCustoms = getItemCustoms?.(item) ?? [];
    const allCustoms = [...(theme?.customs ?? []), ...itemCustoms];
    const similarityCustom = Math.max(0, ...allCustoms.map(e => cosineSimilarity(termEmbedding, e)));
    const similarityColor = colorSimilarity(termEmbedding, theme?.colors ?? [], white, beige);

    const similarity = Math.max(
      similarityLexical * 1.01, // A perfect lexical match ranks above all else. *1.01 to tie-break.
      similarityConcept,
      similarityColor,
      similarityCustom,
    );

    similarityMap.set(themeId, similarity);
    similarityTiebreakMap.set(themeId, conceptSumScore);

    if (collectScores) {
      const weightedLexical = similarityLexical * 1.01;
      // Mirrors the Math.max precedence above, so the reported winner is the
      // factor that actually produced `similarity`. Safe to compare with `===`:
      // Math.max returns one of its arguments unchanged, and `weightedLexical`
      // recomputes the same product from the same operands.
      //
      // An all-zero row reports 'lexical' (0 === 0 matches first), but such rows
      // sit below `threshold` and are filtered out before any tile renders them.
      const winningFactor = weightedLexical === similarity ? 'lexical' : similarityConcept === similarity ? 'concept' : similarityColor === similarity ? 'color' : 'custom';

      collectScores.set(themeId, {
        similarity,
        lexical: similarityLexical,
        weightedLexical,
        concept: similarityConcept,
        color: similarityColor,
        custom: similarityCustom,
        conceptSum: conceptSumScore,
        winningFactor,
      });
    }
  }

  const filtered = items.filter(item => (similarityMap.get(getThemeId(item)) ?? 0) >= threshold);
  const sorted = [...filtered].sort((a, b) => {
    const scoreA = similarityMap.get(getThemeId(a)) ?? 0;
    const scoreB = similarityMap.get(getThemeId(b)) ?? 0;
    const diff = scoreB - scoreA;

    // Precedence 1: primary sort on final similarity. Decides outright unless
    // scores are tied — i.e. multiple items resolve their max via the same
    // embedding (e.g. photo-tagged themes all maxing on the shared `custom`
    // cosine).
    if (Math.abs(diff) >= SIMILARITY_TIE_EPSILON) return diff;

    // Precedence 2: tiebreak by token-level name match. Fuse is configured with
    // `tokenize: true`, so `lexicalScores` already reflects per-token hits on
    // the template name. A name that literally contains a query word beats one
    // that only matches via embeddings.
    const lexicalDiff = (lexicalScores.get(lexicalThemeId(b)) ?? 0) - (lexicalScores.get(lexicalThemeId(a)) ?? 0);
    if (lexicalDiff !== 0) return lexicalDiff;

    // Precedence 3: tiebreak by concept breadth. `conceptSum` rewards themes
    // whose style keywords are broadly (if weakly) related to the query,
    // preferring them over themes that got lucky on one factor but are
    // otherwise off-topic.
    return (similarityTiebreakMap.get(getThemeId(b)) ?? 0) - (similarityTiebreakMap.get(getThemeId(a)) ?? 0);
  });

  return sorted;
}
